import React, { Fragment, memo, useCallback, useContext, useRef, useState } from 'react'
import { ThemeContext } from 'styled-components'
import { NavigationIcon, IconType, SolidButton, GhostButton, Header, ScreenContext } from 'react-components'

import {
    ButtonContentContainer,
    Container,
    LeftContainer,
    LoginIcon,
    LogoutIcon,
    LoginButtonContainer,
    LogInButtonContentContainer,
    LogOutButtonContentContainer,
    LowerContentContainer,
    NavigationIconContainer,
    RightContainer,
    SidebarButtonContainer,
    SidebarContentContainer,
    SidebarLink,
    SidebarLinkContainer,
    LogOutButtonContainer,
    ButtonContainerDesktop,
    ContentContainer,
    Image,
    SideBarLoginButton,
    HeaderSolidButtonContainer,
    ImageContainer,
} from './header-section-styles'
import { HeaderSectionTransport } from '@common/transports/deprecated-home-page-transport'
import NavigationItem from './navigation-item'
import { homePageEventCategories } from '@src/services/analytics-service/categories'
import { AnalyticsService } from '@src/services'
import { homePageEventLabels } from '@src/services/analytics-service/labels'
import Sidebar from '../sidebar'
import SidebarItem from './sidebar-item'

interface Props {
    headerSection: HeaderSectionTransport
    isLoggedIn: boolean
}

const HeaderSection: React.FC<Props> = ({ headerSection: { id, logoImage, navigation, menu }, isLoggedIn }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false)
    const _toggleSidebar = useCallback(() => setIsSidebarOpen(!isSidebarOpen), [isSidebarOpen, setIsSidebarOpen])
    const _headerContainerRef = useRef<HTMLDivElement>(null)
    const theme = useContext(ThemeContext)

    const _handleLogout = useCallback(() => {
        _toggleSidebar()
        // TODO: Handle clear device session cookie.
    }, [_toggleSidebar])

    const { isTablet, isDesktop } = useContext(ScreenContext)

    const { loginButton, logoutButton, dashboardButton, registerButton, buttons } = navigation

    const _handleLogoImageClick = useCallback(() => {
        if (logoImage.eventLabel) {
            AnalyticsService.trackClickEvent(homePageEventCategories.HEADER_SECTION, logoImage.eventLabel)
        }
    }, [logoImage.eventLabel])

    const _handleLoginButtonClick = useCallback(() => {
        if (loginButton.eventLabel) {
            AnalyticsService.trackClickEvent(homePageEventCategories.HEADER_SECTION, loginButton.eventLabel)
        }
    }, [loginButton.eventLabel])

    const _handleRegisterButtonClick = useCallback(() => {
        if (registerButton.eventLabel) {
            AnalyticsService.trackClickEvent(homePageEventCategories.HEADER_SECTION, registerButton.eventLabel)
        }
    }, [registerButton.eventLabel])

    const _handleNavigationIconClick = useCallback(() => {
        _toggleSidebar()
        AnalyticsService.trackClickEvent(
            homePageEventCategories.HEADER_SECTION,
            homePageEventLabels.headerSection.NAVIGATION_MENU,
        )
    }, [_toggleSidebar])

    return (
        <Header id={id}>
            <Container ref={_headerContainerRef}>
                <ContentContainer>
                    <LeftContainer>
                        <ImageContainer href={logoImage.href ?? undefined} onClick={_handleLogoImageClick}>
                            <Image src={logoImage.url} alt={logoImage.alt} />
                        </ImageContainer>
                    </LeftContainer>
                    <RightContainer>
                        {isDesktop && buttons.map(button => <NavigationItem key={button.url} button={button} />)}
                        {isLoggedIn && isDesktop && (
                            <LogOutButtonContainer>
                                <GhostButton href={logoutButton.url} padding="0" width={theme.layout.fill}>
                                    <LogOutButtonContentContainer>
                                        <LogoutIcon />
                                        {logoutButton.text}
                                    </LogOutButtonContentContainer>
                                </GhostButton>
                            </LogOutButtonContainer>
                        )}
                        {isLoggedIn && isDesktop && (
                            <ButtonContainerDesktop>
                                <SolidButton href={dashboardButton.url} padding="0" width={theme.layout.fill}>
                                    <ButtonContentContainer>{dashboardButton.text}</ButtonContentContainer>
                                </SolidButton>
                            </ButtonContainerDesktop>
                        )}
                        {!isLoggedIn && (isTablet || isDesktop) && (
                            <LoginButtonContainer>
                                <GhostButton
                                    href={loginButton.url}
                                    padding="0"
                                    width={theme.layout.fill}
                                    onClick={_handleLoginButtonClick}
                                >
                                    <LogInButtonContentContainer>
                                        <LoginIcon />
                                        {loginButton.text}
                                    </LogInButtonContentContainer>
                                </GhostButton>
                            </LoginButtonContainer>
                        )}
                        {!isLoggedIn && (
                            <HeaderSolidButtonContainer>
                                <SolidButton
                                    href={registerButton.url}
                                    padding="0"
                                    width={theme.layout.fill}
                                    onClick={_handleRegisterButtonClick}
                                >
                                    <ButtonContentContainer>{registerButton.text}</ButtonContentContainer>
                                </SolidButton>
                            </HeaderSolidButtonContainer>
                        )}
                        {!isDesktop && (
                            <NavigationIconContainer>
                                <NavigationIcon
                                    iconType={isSidebarOpen ? IconType.CLOSE_MENU : IconType.OPEN_MENU}
                                    title={menu.text}
                                    onClick={_handleNavigationIconClick}
                                />
                            </NavigationIconContainer>
                        )}
                    </RightContainer>
                </ContentContainer>
            </Container>
            <Sidebar
                isVisible={isSidebarOpen}
                top={_headerContainerRef.current ? `${_headerContainerRef.current.offsetHeight}px` : 0}
                handleOutsideClick={_toggleSidebar}
                maxWidth={'22.5rem'}
            >
                <SidebarContentContainer>
                    <div>
                        {buttons.map(button => (
                            <SidebarItem
                                key={button.url}
                                onClick={_toggleSidebar}
                                item={button}
                                eventCategory={homePageEventCategories.HEADER_SECTION}
                            />
                        ))}
                    </div>
                    <LowerContentContainer>
                        {isLoggedIn ? (
                            <Fragment>
                                <SidebarButtonContainer>
                                    <SolidButton
                                        onClick={_toggleSidebar}
                                        href={dashboardButton.url}
                                        width={theme.layout.fill}
                                    >
                                        {dashboardButton.text}
                                    </SolidButton>
                                </SidebarButtonContainer>
                                <SidebarLinkContainer>
                                    <SidebarLink onClick={_handleLogout} href={logoutButton.url}>
                                        {logoutButton.text}
                                    </SidebarLink>
                                </SidebarLinkContainer>
                            </Fragment>
                        ) : (
                            <Fragment>
                                <SidebarButtonContainer>
                                    <GhostButton
                                        onClick={_toggleSidebar}
                                        href={loginButton.url}
                                        width={theme.layout.fill}
                                    >
                                        <SideBarLoginButton>
                                            <LoginIcon />
                                            {loginButton.text}
                                        </SideBarLoginButton>
                                    </GhostButton>
                                </SidebarButtonContainer>
                                <SidebarButtonContainer>
                                    <SolidButton
                                        onClick={_toggleSidebar}
                                        href={registerButton.url}
                                        width={theme.layout.fill}
                                    >
                                        {registerButton.text}
                                    </SolidButton>
                                </SidebarButtonContainer>
                            </Fragment>
                        )}
                    </LowerContentContainer>
                </SidebarContentContainer>
            </Sidebar>
        </Header>
    )
}

export default memo(HeaderSection)
